import { Alert, Box, Group, Tabs } from "@mantine/core";
import React from "react";
import { useHistory } from "react-router-dom";
import { useAppSelector } from "../../../hooks";
import ChecklistBoardSelector from "./ChecklistBoardSelector";
import { TypeOfChecklistEnum } from "./ChecklistConstants";
import { useGetProjectChecklistTemplatesQuery, useGetProjectChecklistsQuery } from "./checklistRTK";

const PROJECT_TEMPLATE_HELP_URL =
  "https://multiconsultas.sharepoint.com/sites/DSP/SitePages/Hvordan%20Dokumentkontroll%20utf%C3%B8res%20i%20CORE.aspx?isSPOFile=1&xsdata=MDV8MDJ8fGVhMDk3ZDJkYjgyMTQ1MTQzOWRlMDhkZWYxM2EyYWIzfDgxYWI3NDc4OGQzYzQ3MzY4YjU0N2YyYmZkOWZhMjBlfDB8MHw2MzkyMTM0MjkyNTk5NzUxMzR8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKRFFTSTZJbFJsWVcxelgwRlVVRk5sY25acFkyVmZVMUJQVEU5R0lpd2lWaUk2SWpBdU1DNHdNREF3SWl3aVVDSTZJbGRwYmpNeUlpd2lRVTRpT2lKUGRHaGxjaUlzSWxkVUlqb3hNWDA9fDF8TDJOb1lYUnpMekU1T2pBd1kyTm1OamxoTFRBNVpqVXROR0V4WXkwNFpXVmpMV1ZqTURNeVlXUTNPVGRtWTE5ak1HTTNZVFUxWlMwNU1qZzVMVFF3WTJVdFlXUmxaQzFqWkdJMFpHSTBPV013TmpaQWRXNXhMbWRpYkM1emNHRmpaWE12YldWemMyRm5aWE12TVRjNE5UYzBOakV5TlRVMk9RPT18M2ViNzc1MzA4YjMzNDc0OGQ3YTQwOGRlZjEzYTJhYjJ8OTQ3NDMxZDI4MzhjNDM5NzkyZTYzMTEyZWUxODMxMGU%3D&sdata=YXhyK2RkYzdiTFRhUTMxdzJkNE1uWEhINmlGWXBkaSs4OEpmTHl0S1dlaz0%3D&ovuser=81ab7478-8d3c-4736-8b54-7f2bfd9fa20e%2Cmartinm%40multiconsult.no&TeamsCID=7b76a2b9-cdab-47e1-9818-3ac5be79465a&OR=Teams-HL&CT=1785746354736&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiI0OS8yNjA3MDIxNTcxMSJ9&linkOpenTime=1785746354742&SafelinksUrl=https%3A%2F%2Fmulticonsultas.sharepoint.com%2Fsites%2FDSP%2FSitePages%2FHvordan%2520Dokumentkontroll%2520utf%25C3%25B8res%2520i%2520CORE.aspx";

const ChecklistBoards: React.FC<{ typeOfChecklist: TypeOfChecklistEnum }> = ({ typeOfChecklist }) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const isTemplateProject = useAppSelector((state) => state.adminReducer.project?.maconomy?.projectNo === 100);
  const history = useHistory();

  const changeTypeOfChecklistInUrl = (_typeOfChecklist: TypeOfChecklistEnum) => {
    history.push(`/project/${project._id}/checklist/${_typeOfChecklist}`);
  };

  return (
    <Box w={"100%"} overflow="hidden">
      {isTemplateProject === true && (
        <Alert className="m-2" color="yellow" variant="light">
          Alle sjekklister i dette prosjektet vil være tilgjengelige som maler i andre prosjekter!
        </Alert>
      )}
      <Box w={"100%"} px={"xs"} pt={"xs"}>
        <Group
          w="100%"
          justify="flex-start"
          align="end"
          wrap="nowrap"
          style={{ boxShadow: "inset 0 -2px 0 0 #e4e7eb" }}
        >
          <Tabs
            value={typeOfChecklist}
            onChange={(value) =>
              changeTypeOfChecklistInUrl((value ?? TypeOfChecklistEnum.CHECKLIST) as TypeOfChecklistEnum)
            }
            styles={{ tab: { fontWeight: 600 } }}
          >
            <Tabs.List>
              <Tabs.Tab style={{ fontSize: "1.05em", fontWeight: 400 }} value={TypeOfChecklistEnum.CHECKLIST}>
                Sjekklister
              </Tabs.Tab>
              {isTemplateProject === false && (
                <Tabs.Tab style={{ fontSize: "1.05em", fontWeight: 400 }} value={TypeOfChecklistEnum.TEMPLATE}>
                  Prosjektmaler
                </Tabs.Tab>
              )}
            </Tabs.List>
          </Tabs>
        </Group>
      </Box>
      {typeOfChecklist === TypeOfChecklistEnum.CHECKLIST && <ChecklistInstanceBoardSelector />}
      {typeOfChecklist === TypeOfChecklistEnum.TEMPLATE && (
        <ChecklistTemplateBoardSelector projectTemplateHelpUrl={PROJECT_TEMPLATE_HELP_URL} />
      )}
    </Box>
  );
};

export default ChecklistBoards;

const ChecklistInstanceBoardSelector: React.FC<{}> = ({}) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const { selectedPnsFilterItem, freeTextSearch } = useAppSelector((state) => state.checklistReducer);

  const {
    data: projectChecklists = [],
    isLoading: checklistsLoading,
    isError: checklistsError,
  } = useGetProjectChecklistsQuery({
    projectId: project._id,
    freeTextSearch,
  });

  const templateType = project.maconomy?.projectNo === 100 ? "MC" : "";

  const filtredByPns =
    selectedPnsFilterItem != null && selectedPnsFilterItem !== ""
      ? projectChecklists.filter((checklist) =>
          checklist.pns?.toLowerCase().startsWith(selectedPnsFilterItem.toLowerCase()),
        )
      : projectChecklists;

  return (
    <ChecklistBoardSelector
      name="ChecklistInstanceBoardSelector"
      templateType={templateType}
      checklists={filtredByPns}
      loading={checklistsLoading}
      error={checklistsError}
      createNewTooltip="Start sjekkliste"
    />
  );
};

const ChecklistTemplateBoardSelector: React.FC<{ projectTemplateHelpUrl?: string }> = ({ projectTemplateHelpUrl }) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const { freeTextSearch } = useAppSelector((state) => state.checklistReducer);

  const {
    data: projectTemplates = [],
    isLoading: templatesLoading,
    isError: templatesError,
  } = useGetProjectChecklistTemplatesQuery({
    projectId: project._id,
    freeTextSearch,
  });

  const templateType = project.maconomy?.projectNo === 100 ? "MC" : "PROJECT";

  return (
    <ChecklistBoardSelector
      name="ChecklistTemplateBoardSelector"
      templateType={templateType}
      checklists={projectTemplates}
      loading={templatesLoading}
      error={templatesError}
      createNewTooltip="Opprett oppdragsspesifikk sjekkliste"
      projectTemplateHelpUrl={projectTemplateHelpUrl}
    />
  );
};
